import { Amplify } from "aws-amplify"
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  fetchUserAttributes,
  fetchAuthSession,
  getCurrentUser,
  updateUserAttributes,
} from "aws-amplify/auth"
import { exists, window } from "browser-monads"
import GTM from "@src/services/gtm"

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.GATSBY_AWS_USER_POOL_ID,
      userPoolClientId: process.env.GATSBY_AWS_USER_POOL_WEB_CLIENT_ID,
    },
  },
})

const isBrowser = () => exists(window)

// ponytail: returns [{Name, Value}] array to match v5 shape so user-context.js stays unchanged
const getUser = async (baseObject = false) => {
  if (!isBrowser()) {
    throw new Error("User is not available outside of browser.")
  }
  try {
    const currentUser = await getCurrentUser()
    if (baseObject) {
      return currentUser
    }
    GTM.dataLayerPush({
      event: "10a.user.authentication.check",
      "user-authenticated": true,
    })
    const attrs = await fetchUserAttributes()
    return Object.entries(attrs).map(([Name, Value]) => ({ Name, Value }))
  } catch (e) {
    GTM.dataLayerPush({
      event: "10a.user.authentication.check",
      "user-authenticated": false,
    })
    return undefined
  }
}

const getUserToken = async () => {
  const session = await fetchAuthSession()
  const token = session.tokens?.idToken?.toString()
  if (!token) throw new Error("User token could not be found.")
  return token
}

const updateAttributes = async updates => {
  await getCurrentUser()
  await updateUserAttributes({ userAttributes: updates })
  return true
}

const signin = async (username, password) => {
  try {
    await amplifySignIn({ username, password })
    GTM.dataLayerPush({
      event: "10a.user.signin.success",
      "user-authenticated": true,
    })
    return true
  } catch (err) {
    GTM.dataLayerPush({
      event: "10a.user.signin.failed",
      "user-authenticated": false,
    })
    throw err.message || JSON.stringify(err)
  }
}

const signout = async () => {
  try {
    await amplifySignOut({ global: true })
    GTM.dataLayerPush({
      event: "10a.user.signout.success",
      "user-authenticated": false,
    })
    return true
  } catch (err) {
    GTM.dataLayerPush({
      event: "10a.user.signout.failed",
    })
    throw err.message || JSON.stringify(err)
  }
}

const signup = async (username, password) => {
  try {
    await amplifySignUp({
      username,
      password,
      options: {
        userAttributes: { email: username },
      },
    })
    GTM.dataLayerPush({
      event: "10a.user.signup.success",
    })
    return true
  } catch (err) {
    GTM.dataLayerPush({
      event: "10a.user.signup.failed",
    })
    throw err.message || JSON.stringify(err)
  }
}

const verifyAccount = async (username, code) => {
  try {
    await confirmSignUp({ username, confirmationCode: code })
    GTM.dataLayerPush({
      event: "10a.user.verification.success",
    })
    return true
  } catch (err) {
    GTM.dataLayerPush({
      event: "10a.user.verification.failed",
    })
    throw err.message || JSON.stringify(err)
  }
}

const verificationResend = async username => {
  try {
    await resendSignUpCode({ username })
    GTM.dataLayerPush({
      event: "10a.user.verification-resend.success",
    })
    return true
  } catch (err) {
    GTM.dataLayerPush({
      event: "10a.user.verification-resend.failed",
    })
    throw err.message || JSON.stringify(err)
  }
}

const recoverAccount = async username => {
  try {
    await resetPassword({ username })
    GTM.dataLayerPush({
      event: "10a.user.account-recovery.success",
    })
    return true
  } catch (err) {
    GTM.dataLayerPush({
      event: "10a.user.account-recovery.failed",
    })
    throw err.message || JSON.stringify(err)
  }
}

const recoverPassword = async (username, code, newPassword) => {
  try {
    await confirmResetPassword({
      username,
      confirmationCode: code,
      newPassword,
    })
    // ponytail: sign out after password reset so user must re-login with new password
    await amplifySignOut().catch(() => null)
    GTM.dataLayerPush({
      event: "10a.user.password-reset.success",
    })
    return true
  } catch (err) {
    GTM.dataLayerPush({
      event: "10a.user.password-reset.failed",
    })
    throw err.message || JSON.stringify(err)
  }
}

const AuthService = {
  getUser,
  getUserToken,
  signin,
  signout,
  signup,
  recoverAccount,
  recoverPassword,
  verifyAccount,
  verificationResend,
  updateAttributes,
}
export default AuthService
